import { useRef } from "react";
import Button from "../../../shared/components/Button";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import TnpOrganizerLogo from "../../../shared/components/TnpOrganizerLogo";
import UserAvatar from "../../../shared/components/UserAvatar";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { EventIdentityMode } from "../../challenges/types/challengeAdmin";

type AdminEventIdentityFieldsProps = {
  value: EventIdentityMode;
  onChange: (value: EventIdentityMode) => void;
  organizerName?: string | null;
  organizerAvatarUrl?: string | null;
  organizerLogoUrl?: string | null;
  disabled?: boolean;
  hideHeader?: boolean;
  allowTnpStandard?: boolean;
  canManageLogo?: boolean;
  logoSaving?: boolean;
  onLogoError?: (message: string) => void;
  onLogoUpload?: (logoUrl: string) => void | Promise<void>;
};

export default function AdminEventIdentityFields({
  value,
  onChange,
  organizerName,
  organizerAvatarUrl,
  organizerLogoUrl,
  disabled = false,
  hideHeader = false,
  allowTnpStandard = false,
  canManageLogo = false,
  logoSaving = false,
  onLogoError,
  onLogoUpload,
}: AdminEventIdentityFieldsProps) {
  const texts = siteTexts.admin.createChallengePage.eventIdentity;
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const hasLogo = Boolean(String(organizerLogoUrl || "").trim());
  const canUploadLogo = canManageLogo && Boolean(onLogoUpload) && !disabled;
  const options: Array<{ mode: EventIdentityMode; label: string; help: string }> =
    [
      { mode: "organizer_photo", label: texts.photo, help: texts.photoHelp },
      { mode: "organizer_logo", label: texts.logo, help: texts.logoHelp },
      ...(allowTnpStandard
        ? [{ mode: "tnp_standard" as const, label: texts.tnp, help: texts.tnpHelp }]
        : []),
    ];
  const safeValue =
    value === "tnp_standard" && !allowTnpStandard ? "organizer_photo" : value;
  const previewMode =
    safeValue === "organizer_logo" && !hasLogo ? "organizer_photo" : safeValue;

  const handleLogoFile = (file?: File | null) => {
    if (!file || !onLogoUpload) return;
    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type) || file.size > 2 * 1024 * 1024) {
      onLogoError?.(texts.logoInvalid);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      void onLogoUpload(String(reader.result || ""));
    };
    reader.onerror = () => onLogoError?.(texts.logoInvalid);
    reader.readAsDataURL(file);
  };

  return (
    <section className="admin-event-identity">
      {!hideHeader && (
        <div className="admin-config-section-marker">
          <h4 className="info-label">
            {texts.title}
            <InfoTooltip content={texts.info} />
          </h4>
          <p>{texts.description}</p>
        </div>
      )}
      <div className="admin-event-identity-grid">
        <fieldset className="admin-event-identity-options">
          <legend>{texts.mode}</legend>
          {options.map((option) => {
            const logoDisabled = option.mode === "organizer_logo" && !hasLogo;
            return (
              <label className="admin-event-identity-option" key={option.mode}>
                <input
                  className="admin-event-identity-radio"
                  type="radio"
                  name="event-identity-mode"
                  checked={safeValue === option.mode}
                  disabled={disabled || logoDisabled}
                  onChange={() => onChange(option.mode)}
                />
                <span className="admin-event-identity-option-copy">
                  {option.label}
                  <small>{option.help}</small>
                </span>
              </label>
            );
          })}
          {!hasLogo && <p className="status-message">{texts.logoMissing}</p>}
          {canUploadLogo && (
            <div className="admin-event-identity-logo-actions">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(event) => {
                  handleLogoFile(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <Button
                type="button"
                variant="pill"
                disabled={logoSaving}
                onClick={() => logoInputRef.current?.click()}
              >
                {logoSaving
                  ? texts.savingLogo
                  : hasLogo
                    ? texts.replaceLogo
                    : texts.uploadLogo}
              </Button>
              <small>{texts.logoUploadHelp}</small>
            </div>
          )}
        </fieldset>
        <div className="admin-event-identity-preview" aria-label={texts.preview}>
          <span>{texts.preview}</span>
          <div className="admin-event-identity-card">
            {previewMode === "tnp_standard" ? (
              <TnpOrganizerLogo size="compact" />
            ) : previewMode === "organizer_logo" ? (
              <img src={organizerLogoUrl || ""} alt={texts.logoPreviewAlt} />
            ) : (
              <UserAvatar
                name={organizerName || texts.organizerFallback}
                avatarUrl={organizerAvatarUrl || undefined}
                size={44}
              />
            )}
            <strong>{organizerName || texts.organizerFallback}</strong>
          </div>
        </div>
      </div>
    </section>
  );
}
